/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React from 'react';
import { oneOf, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import {
  propTypes,
  LISTING_UNIT_TYPES,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
} from '../../util/types';

import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';
import LineItemUnknownItemsMaybe from './LineItemUnknownItemsMaybe';
import { ReactComponent as InfoIcon } from '../../assets/icons/infoOutline.svg';

import css from './OrderBreakdown.module.css';
import { formatMoney } from '../../util/currency';
import { useConfiguration } from '../../context/configurationContext';

import { ReactComponent as Checked } from '../../assets/icons/checked.svg';
import { ReactComponent as NotChecked } from '../../assets/icons/not-checked.svg';

const ANIMAL_CONTENT_CREATOR = 'animalContentCreator';

export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    booking,
    intl,
    dateType,
    timeZone,
    currency,
    marketplaceName,
    isFromEstimate,
  } = props;

  const config = useConfiguration();

  const ALLOWED_ADD_ON_FOR_OTHERS = config.listing.allowedAddonForOthers || [];
  const ALLOWED_ADD_ON_FOR_CONTENT_CREATORS = config.listing.allowedAddOnForContentCreator || [];

  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  const lineItems = transaction.attributes.lineItems;
  const unitLineItem = lineItems?.find(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );
  // Line-item code that matches with base unit: day, night, hour, item
  const lineItemUnitType = unitLineItem?.code;

  const hasCommissionLineItem = lineItems.find(item => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const classes = classNames(rootClassName || css.root, className);

  /**
   * OrderBreakdown contains different line items:
   *
   * LineItemBookingPeriod: prints booking start and booking end types. Prop dateType
   * determines if the date and time or only the date is shown
   *
   * LineItemShippingFeeMaybe: prints the shipping fee (combining additional fee of
   * additional items into it).
   *
   * LineItemShippingFeeRefundMaybe: prints the amount of refunded shipping fee
   *
   * LineItemBasePriceMaybe: prints the base price calculation for the listing, e.g.
   * "$150.00 * 2 nights $300"
   *
   * LineItemUnknownItemsMaybe: prints the line items that are unknown. In ideal case there
   * should not be unknown line items. If you are using custom pricing, you should create
   * new custom line items if you need them.
   *
   * LineItemSubTotalMaybe: prints subtotal of line items before possible
   * commission or refunds
   *
   * LineItemRefundMaybe: prints the amount of refund
   *
   * LineItemCustomerCommissionMaybe: prints the amount of customer commission
   * The default transaction process used by this template doesn't include the customer commission.
   *
   * LineItemCustomerCommissionRefundMaybe: prints the amount of refunded customer commission
   *
   * LineItemProviderCommissionMaybe: prints the amount of provider commission
   *
   * LineItemProviderCommissionRefundMaybe: prints the amount of refunded provider commission
   *
   * LineItemTotalPrice: prints total price of the transaction
   *
   */

  const selectedPackage = transaction?.attributes?.protectedData?.selectedPackage;
  const providedService = transaction?.attributes?.protectedData?.providedService;
  const updatedOffering = transaction?.attributes?.protectedData?.updatedOffering;
  const updatedPrice = transaction?.attributes?.protectedData?.updatedPrice;

  const {
    addons: addonsForCustom,
    description: descriptionForCustom,
    extraAddons: extraAddOnForCustom,
  } = updatedOffering || {};

  const listing = transaction?.listing || {};

  const selectedPackageDetails = listing?.attributes?.publicData?.packageDetail[selectedPackage];
  const category = listing?.attributes?.publicData?.category;

  const isContentCreartor = category === ANIMAL_CONTENT_CREATOR;

  const addonToDisplay = isContentCreartor
    ? ALLOWED_ADD_ON_FOR_CONTENT_CREATORS
    : ALLOWED_ADD_ON_FOR_OTHERS;

  return (
    <div className={classes}>
      {!isFromEstimate && selectedPackage && (
        <div className={css.priceContainer}>
          <>
            <div className={css.pckgNm}>
              {selectedPackage === 'advance' ? 'Advanced' : selectedPackage}
            </div>
            <div className={css.price}>
              {formatMoney(intl, unitLineItem.unitPrice)}
              <span className={css.unit}>
                {isContentCreartor ? (
                  <FormattedMessage id="OrderBreakdown.pricePerJob" />
                ) : (
                  <FormattedMessage id="OrderBreakdown.pricePerDay" />
                )}
              </span>
              <span className={css.iconContainer}>
                <InfoIcon />
              </span>
            </div>
          </>
        </div>
      )}

      {!isFromEstimate && updatedPrice && (
        <div className={css.priceContainer}>
          <>
            <div className={css.pckgNm}>Custom price</div>
            <div className={css.price}>
              {formatMoney(intl, unitLineItem.unitPrice)}
              <span className={css.unit}>
                {isContentCreartor ? (
                  <FormattedMessage id="OrderBreakdown.pricePerJob" />
                ) : (
                  <FormattedMessage id="OrderBreakdown.pricePerDay" />
                )}
              </span>
              <span className={css.iconContainer}>
                <InfoIcon />
              </span>
            </div>
          </>
        </div>
      )}

      <LineItemBookingPeriod
        booking={booking}
        code={lineItemUnitType}
        dateType={dateType}
        timeZone={timeZone}
        isContentCreartor={isContentCreartor}
      />

      {!isFromEstimate && selectedPackage && (
        <div className={css.descriptionContainer}>
          <div className={css.description}>{selectedPackageDetails?.description}</div>
        </div>
      )}
      {!isFromEstimate && descriptionForCustom && (
        <div className={css.descriptionContainer}>
          <div className={css.description}>{descriptionForCustom}</div>
        </div>
      )}

      {!isFromEstimate && addonsForCustom && (
        <div className={css.addOnContainer}>
          {addonToDisplay.map(({ label, key, value }) => (
            <div className={css.pckgRow} key={key}>
              <div className={classNames(css.pckg, css.iconContainer)}>
                {addonsForCustom?.includes(key) ? (
                  <span>
                    <Checked /> {label}
                  </span>
                ) : (
                  <span>
                    <NotChecked /> {label}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!isFromEstimate && selectedPackage && (
        <div className={css.addOnContainer}>
          {addonToDisplay.map(({ label, key, value }) => (
            <div className={css.pckgRow} key={key}>
              <div className={classNames(css.pckg, css.iconContainer)}>
                {providedService?.includes(key) ? (
                  <span>
                    <Checked /> {label}
                  </span>
                ) : (
                  <span>
                    <NotChecked /> {label}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <LineItemBasePriceMaybe
        lineItems={lineItems}
        code={lineItemUnitType}
        intl={intl}
        isContentCreartor={isContentCreartor}
      />
      <LineItemShippingFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemPickupFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemUnknownItemsMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />

      <LineItemSubTotalMaybe
        lineItems={lineItems}
        code={lineItemUnitType}
        userRole={userRole}
        intl={intl}
        marketplaceCurrency={currency}
      />
      <LineItemRefundMaybe lineItems={lineItems} intl={intl} marketplaceCurrency={currency} />

      <LineItemCustomerCommissionMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemCustomerCommissionRefundMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemProviderCommissionMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemProviderCommissionRefundMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemTotalPrice transaction={transaction} isProvider={isProvider} intl={intl} />

      {hasCommissionLineItem ? (
        <span className={css.feeInfo}>
          <FormattedMessage id="OrderBreakdown.commissionFeeNote" />
        </span>
      ) : null}
    </div>
  );
};

OrderBreakdownComponent.defaultProps = {
  rootClassName: null,
  className: null,
  booking: null,
  dateType: null,
};

OrderBreakdownComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  userRole: oneOf(['customer', 'provider']).isRequired,
  transaction: propTypes.transaction.isRequired,
  booking: propTypes.booking,
  dateType: propTypes.dateType,

  // from injectIntl
  intl: intlShape.isRequired,
};

const OrderBreakdown = injectIntl(OrderBreakdownComponent);

OrderBreakdown.displayName = 'OrderBreakdown';

export default OrderBreakdown;
